import { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { TextField } from "@mui/material"
import { toUpper } from "lodash"
import { getRealValue, numericValueRegex, numericValueWithSuffixRegex, valueFormatter } from "./helpers"
import { KEY_CODES } from "helpers/keyboard"

export default function FactorConstraintInput({ id, autoFocus, placeholder, onChange, onFocus, onEnterKey, initialValue, supportMultiplierSuffix }) {
  const formatValue = value => supportMultiplierSuffix ? valueFormatter(value) : value || ""
  const inputRef = useRef(null)
  const [displayValue, setDisplayValue] = useState(formatValue(initialValue))
  const [cursor, setCursor] = useState(0)
  const [errorMessage, setErrorMessage] = useState(false)

  useEffect(() => {
    const input = inputRef.current
    if (input) {
      input.setSelectionRange(cursor, cursor)
    }
  }, [inputRef, cursor])

  function validateValue(value) {
    if ((supportMultiplierSuffix && !numericValueWithSuffixRegex.test(value)) || (!supportMultiplierSuffix && !numericValueRegex.test(value))) {
      setErrorMessage("Invalid numeric value")
      return false
    }
    setErrorMessage("")
    return true
  }

  function handleChange({ target: { value: newValue, selectionStart } }) {
    setDisplayValue(newValue)
    setCursor(selectionStart)

    const isValid = validateValue(newValue)
    if (onChange) {
      let value = null
      if (isValid) {
        setDisplayValue(toUpper(newValue))
        value = getRealValue(newValue)
      }
      onChange({ id, value })
    }
  }

  function handleKeyDown({ keyCode, target }) {
    switch (keyCode) {
      case KEY_CODES.ENTER:
        if (onEnterKey) {
          onEnterKey()
        }
        target.blur()
        return
      case KEY_CODES.ESCAPE:
        setDisplayValue(formatValue(initialValue))
        target.blur()
    }
  }

  return <TextField
    variant="standard"
    {...{
      id,
      inputRef,
      autoFocus,
      placeholder,
      value: displayValue,
      onChange: handleChange,
      onFocus,
      onKeyDown: handleKeyDown,
      error: !!errorMessage,
      helperText: errorMessage,
    }}
  />
}

FactorConstraintInput.propTypes = {
  id: PropTypes.string.isRequired,
  autoFocus: PropTypes.bool,
  placeholder: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onEnterKey: PropTypes.func,
  supportMultiplierSuffix: PropTypes.bool,
  toggleError: PropTypes.func,
}
